import * as utils from "../components/utils";
import introJs from "intro.js";
import "intro.js/minified/introjs.min.css";

const SCRIPT_NAME = "init_intro.js";

const GUIDE_USED_COOKIE = "intro.js_used"; // Name des Cookies, das den Status speichert

// Funktion, um zu prüfen, ob der Welcome-Step angezeigt werden soll
const shouldShowWelcomeStep = () => {
    return !cookies.getCookie(GUIDE_USED_COOKIE);
};



/** Element-Existenz prüfen */
const elementExists = (selector) => {
    const ELEMENT = document.querySelector(selector);
    utils.consoleLog(SCRIPT_NAME, `Prüfe Element: ${selector}`, ELEMENT ? "gefunden" : "nicht gefunden");
    return !!ELEMENT;
};

/** Intro.js Standardoptionen */
const DEFAULT_OPTIONS = {
    showProgress: true,
    showBullets: false,
    exitOnOverlayClick: false,
    tooltipClass: "eThemis-Tooltip",
    doneLabel: utils.getLanguageText("guide.btn.done"),
    nextLabel: '<i class="fa-fw fa-regular fa-arrow-right text-white"></i>',
    prevLabel: '<i class="fa-fw fa-regular fa-arrow-left text-white"></i>',
    disableInteraction: false,
};

const FALLBACK_STEP = [
    {
        title: utils.getLanguageText("guide.fallback.title"),
        intro: utils.getLanguageText("guide.fallback.intro"),
        position: "floating",
    },
];

/** Schritte mit Welcome- und Fallback-Step kombinieren */
/** Schritte mit Welcome- und Fallback-Step kombinieren */
const getDefaultSteps = (steps = []) => {
    const WELCOME_BUTTON_SELECTOR = '[data-intro-js-tour-element-to-trigger="startTour"]';

    const WELCOME_STEP = elementExists(WELCOME_BUTTON_SELECTOR) && shouldShowWelcomeStep()
        ? [
            {
                element: WELCOME_BUTTON_SELECTOR,
                title: utils.getLanguageText("guide.welcome.title"),
                intro: utils.getLanguageText("guide.welcome.intro"),
                position: "bottom",
            },
        ]
        : [];

    // Wenn es keine Schritte gibt, Welcome- und Fallback-Steps zurückgeben
    if (steps.length === 0) {
        return [...WELCOME_STEP, ...FALLBACK_STEP];
    }

    // Nur Welcome-Step + definierte Schritte zurückgeben (kein Fallback)
    return [...WELCOME_STEP, ...steps];
};

/** Automatisch Tour-Schritte generieren */
const generateTourSteps = (currentPage) => {
    const ELEMENTS = document.querySelectorAll("[data-intro-js-tour]");

    // If no ELEMENTS are present, return only the fallback
    if (ELEMENTS.length === 0) {
        utils.consoleLog(SCRIPT_NAME, `Keine Tour-Elemente für Seite "${currentPage}" gefunden.`);
        return getDefaultSteps([]); // Keine zusätzlichen Steps -> Welcome + Fallback
    }

    // Filtere die Elemente und vermeide Duplikate basierend auf Keys
    const USED_KEYS = new Set();

    const STEPS = Array.from(ELEMENTS)
        .map((el) => {
            const KEY = el.getAttribute(
                "data-intro-js-tour");
            if (!KEY || USED_KEYS.has(KEY)) return null; // Überspringe Duplikate
            USED_KEYS.add(KEY); // Markiere den Schlüssel als benutzt

            return {
                element: `[data-intro-js-tour="${KEY}"]`,
                title: utils.getLanguageText(`guide.${currentPage}.${KEY}.title`),
                intro: utils.getLanguageText(`guide.${currentPage}.${KEY}.intro`),
            };
        })
        .filter(Boolean); // Entferne Nullwerte oder undefinierte Einträge

    utils.consoleLog(SCRIPT_NAME, `Generierte Schritte für "${currentPage}":`, STEPS);

    // Rückgabe der definierten Schritte ohne Fallback (da mindestens ein Schritt vorhanden ist)
    return getDefaultSteps(STEPS);
};

/** Akkordeon-Interaktion für Intro.js konfigurieren */
const configureAccordionBehaviour = (introJsInstance, elementToTrigger, stepToTrigger) => {
    introJsInstance.onbeforechange(function () {
        if (this._currentStep === stepToTrigger) {
            return new Promise((resolve) => {
                const ELEMENT_TO_COLLAPSE = document.querySelector(elementToTrigger);
                if (ELEMENT_TO_COLLAPSE && !ELEMENT_TO_COLLAPSE.classList.contains("show")) {
                    try {
                        if (typeof bootstrap !== "undefined") {
                            new bootstrap.Collapse(ELEMENT_TO_COLLAPSE).show();
                        } else {
                            ELEMENT_TO_COLLAPSE.classList.add("show");
                        }
                        setTimeout(resolve, 300); // Zeit für die Animation
                    } catch (error) {
                        utils.consoleLog(SCRIPT_NAME, "Fehler beim Öffnen des Akkordeons:", error);
                    }
                } else {
                    resolve();
                }
            }).catch((error) => {
                utils.consoleLog(SCRIPT_NAME, "Fehler in der Accordion-Promise:", error);
            });
        }
    });
};

/** Dropdown-Interaktion für Intro.js konfigurieren */
const configureDropdownBehaviour = (introJsInstance, dropdownSelector, stepToOpen, stepToClose) => {
    introJsInstance.onbeforechange(function () {
        const DROPDOWN_CONTAINER = document.querySelector(dropdownSelector); // Parent div mit der Klasse "dropdown"
        const DROPDOWN_TOGGLE = DROPDOWN_CONTAINER?.querySelector('[data-bs-toggle="dropdown"]'); // Button im Dropdown

        // Öffnen des Dropdowns
        if (this._currentStep === stepToOpen) {
            return new Promise((resolve) => {
                if (DROPDOWN_TOGGLE) {
                    try {
                        // Prüfen, ob das Dropdown-Element bereits geöffnet ist
                        if (!DROPDOWN_TOGGLE.classList.contains("show")) {
                            if (typeof bootstrap !== "undefined") {
                                const dropdownInstance = new bootstrap.Dropdown(DROPDOWN_TOGGLE); // Bootstrap Dropdown initialisieren
                                dropdownInstance.show();
                            } else {
                                // Fallback für den Fall, dass Bootstrap nicht verfügbar ist
                                DROPDOWN_TOGGLE.classList.add("show");
                                DROPDOWN_CONTAINER.classList.add("show");
                                const DROPDOWN_MENU = DROPDOWN_CONTAINER.querySelector(".dropdown-menu");
                                if (DROPDOWN_MENU) DROPDOWN_MENU.classList.add("show");
                            }
                        }
                        setTimeout(resolve, 300); // Warten, bis die Animation abgeschlossen ist
                    } catch (error) {
                        console.warn("Fehler beim Öffnen des Dropdowns:", error);
                        resolve(); // Setzt die Kette in jedem Fall fort
                    }
                } else {
                    console.warn(`Step ${stepToOpen}: KEIN Dropdown-Toggle gefunden!`);
                    resolve(); // Weiter, wenn kein Toggle-Element gefunden wird
                }
            }).catch((error) => {
                console.warn("Fehler in der Dropdown-Promise:", error);
            });
        }
        // Schließen des Dropdowns, wenn der aktuelle Schritt mit stepsToClose übereinstimmt
        if (stepToClose !== undefined && this._currentStep === stepToClose && DROPDOWN_TOGGLE.classList.contains("show")) {
            try {

                if (typeof bootstrap !== "undefined") {

                    const dropdownInstance = bootstrap.Dropdown.getInstance(DROPDOWN_TOGGLE); // Vorhandene Bootstrap Dropdown-Instanz abrufen
                    if (dropdownInstance) dropdownInstance.hide();
                } else {

                    // Fallback, wenn Bootstrap nicht verfügbar ist
                    DROPDOWN_TOGGLE?.classList.remove("show");
                    DROPDOWN_CONTAINER.classList.remove("show");
                    const DROPDOWN_MENU = DROPDOWN_CONTAINER.querySelector(".dropdown-menu");
                    if (DROPDOWN_MENU) DROPDOWN_MENU.classList.remove("show");
                }
            } catch (error) {
                console.warn("Fehler beim Schließen des Dropdowns:", error);
            }
        }

    });
};
/** Tour starten */
export const startTour = (ACTIVE_GUIDE_PAGES) => {

// TODO: Prüfen, ob sich das Ziel-Element in einem Bootstrap-Komponentencontainer befindet (z. B. Collapse, Modal, Tab), der aktuell nicht sichtbar ist.
// Falls ja, den Container identifizieren und vor dem aktuellen Schritt dessen Sichtbarkeit sicherstellen (z. B. per Toggle oder Show).
// Wenn nachfolgende Schritte außerhalb dieses Containers liegen, soll der Container nach dem letzten Schritt innerhalb wieder geschlossen werden.

    try {
        const CURRENT_PAGE = utils.getCurrentPage(ACTIVE_GUIDE_PAGES);
        const INTRO_JS_INSTANCE = introJs();

        let steps;

        // Prüfe ob die aktuelle Seite in ACTIVE_GUIDE_PAGES vorhanden ist
        if (CURRENT_PAGE) {
            utils.consoleLog(SCRIPT_NAME, `Tour für Seite "${CURRENT_PAGE}" gestartet.`);
            steps = generateTourSteps(CURRENT_PAGE);

            // Füge spezifische Logik für "dashboard" hinzu
            if (CURRENT_PAGE === "dashboard") {
                let stepsToTrigger; // Deklaration der Variable außerhalb der Bedingung

                if (cookies.getCookie(GUIDE_USED_COOKIE)) {
                    stepsToTrigger = 4;
                } else {
                    stepsToTrigger = 6;
                }

                // Verwende STEPS_TO_TRIGGER, nachdem sie korrekt zugewiesen wurde
                configureAccordionBehaviour(INTRO_JS_INSTANCE, '[data-intro-js-tour-element-to-trigger="firstCollapseItem"]', stepsToTrigger);
            }

            // Füge spezifische Logik für "dashboard" hinzu
            if (CURRENT_PAGE === "taskmanager") {
                let stepsToTrigger; // Deklaration der Variable außerhalb der Bedingung

                if (cookies.getCookie(GUIDE_USED_COOKIE)) {
                    stepsToTrigger = 9;
                } else {
                    stepsToTrigger = 10;
                }

                // Verwende STEPS_TO_TRIGGER, nachdem sie korrekt zugewiesen wurde
                configureDropdownBehaviour(INTRO_JS_INSTANCE, '[data-intro-js-tour-element-to-trigger="firstDropdownItem"]', stepsToTrigger, stepsToTrigger + 1);
            }

        } else {
            // Für Seiten, die nicht in ACTIVE_GUIDE_PAGES sind, zeige nur Welcome- und Fallback-Schritte
            utils.consoleLog(SCRIPT_NAME, "Aktuelle Seite nicht in ACTIVE_GUIDE_PAGES. Zeige Welcome- und Fallback-Schritte.");
            steps = getDefaultSteps([]);
        }

        INTRO_JS_INSTANCE.setOptions({
            ...DEFAULT_OPTIONS,
            steps,
        });

        utils.consoleLog(SCRIPT_NAME, "Intro.js Tour gestartet mit folgenden Schritten:", steps);

        INTRO_JS_INSTANCE
            .start()
            .then(() => {
                utils.consoleLog(SCRIPT_NAME, "Tour erfolgreich abgeschlossen.");
                cookies.setCookie(GUIDE_USED_COOKIE, true, 365); // Setze Cookie für 1 Jahr
            })

            .catch((error) => {
                utils.consoleLog(SCRIPT_NAME, "Fehler während der Tour:", error);
            });
    } catch (error) {
        utils.consoleLog(SCRIPT_NAME, "Fehler beim Start der Tour:", error);
    }
};